import { useEffect, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  Handshake,
  Mail,
  Phone,
  Send,
  User,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { useI18n } from "@project/lib/use-i18n";
import { cn } from "@project/lib/utils";
import { csrPartnershipService } from "@project/services/user";
import {
  CSR_AREAS,
  CSR_CONTRIBUTIONS,
  CSR_DURATIONS,
} from "@project/user/components/csr/csr-options";

export interface CsrPartnershipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Focus areas to pre-select, e.g. when opened from a focus-area card. */
  defaultInterests?: string[];
}

const EMPTY_FORM = {
  companyName: "",
  contactPerson: "",
  email: "",
  mobile: "",
  designation: "",
  contribution: "",
  duration: "",
  message: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function IconInput({
  id,
  icon: Icon,
  ...props
}: React.ComponentProps<typeof Input> & { icon: typeof User }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input id={id} className="rounded-xl pl-9" {...props} />
    </div>
  );
}

export function CsrPartnershipModal({
  open,
  onOpenChange,
  defaultInterests = [],
}: CsrPartnershipModalProps) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY_FORM);
  const [interests, setInterests] = useState<string[]>(defaultInterests);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key: keyof typeof EMPTY_FORM) => (value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // Parent opens the modal by prop, so apply pre-selected interests on open.
  useEffect(() => {
    if (open) {
      setInterests(defaultInterests);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (next) {
      return;
    }
    // Reset after the close animation.
    setTimeout(() => {
      setForm(EMPTY_FORM);
      setInterests([]);
      setConsent(false);
      setSubmitted(false);
    }, 300);
  };

  const toggleInterest = (value: string) =>
    setInterests(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const mobileDigits = form.mobile.replace(/\D/g, "");

    if (!form.companyName.trim()) {
      projectToast.error(t("csr.form.validation.companyName"));
      return;
    }
    if (!form.contactPerson.trim()) {
      projectToast.error(t("csr.form.validation.contactPerson"));
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      projectToast.error(t("csr.form.validation.email"));
      return;
    }
    if (mobileDigits.length < 10) {
      projectToast.error(t("csr.form.validation.mobile"));
      return;
    }
    if (interests.length === 0) {
      projectToast.error(t("csr.form.validation.interests"));
      return;
    }
    if (!consent) {
      projectToast.error(t("csr.form.validation.consent"));
      return;
    }

    setSubmitting(true);
    try {
      await csrPartnershipService.create({
        company_name: form.companyName.trim(),
        contact_person_name: form.contactPerson.trim(),
        official_email: form.email.trim(),
        mobile_number: form.mobile.trim(),
        designation: form.designation.trim() || undefined,
        // Keep the order of the options list, not the click order.
        partnership_interests: CSR_AREAS.filter(a =>
          interests.includes(a.value)
        )
          .map(a => a.value)
          .join(", "),
        estimated_contribution: form.contribution || undefined,
        partnership_duration: form.duration || undefined,
        message: form.message.trim() || undefined,
        privacy_consent: true,
      });
      setSubmitted(true);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, t("csr.form.error")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden rounded-3xl border-border bg-card p-0 sm:max-w-2xl">
        {submitted ? (
          <div className="space-y-4 overflow-y-auto px-6 py-12 text-center sm:px-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <DialogTitle className="font-serif text-2xl font-bold text-foreground">
              {t("csr.form.successTitle")}
            </DialogTitle>
            <DialogDescription className="mx-auto max-w-md text-sm leading-relaxed">
              {t("csr.form.successMessage")}
            </DialogDescription>
            <Button
              className="mt-4 rounded-full px-8"
              onClick={() => handleOpenChange(false)}
            >
              {t("csr.form.close")}
            </Button>
          </div>
        ) : (
          <>
            {/* Header and footer stay fixed; only the fields scroll. */}
            <DialogHeader className="shrink-0 space-y-2 border-b border-border px-6 pb-4 pr-12 pt-6 text-left sm:px-8 sm:pt-7 bg-white">
              <div className="bg-gold/15 inline-flex items-center gap-2 self-start rounded-full px-3 py-1 text-xs font-bold text-primary">
                <Handshake className="h-3.5 w-3.5" />
                <span>{t("csr.form.badge")}</span>
              </div>
              <DialogTitle className="font-serif text-2xl font-bold text-foreground">
                {t("csr.form.title")}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {t("csr.form.subtitle")}
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="flex min-h-0 flex-1 flex-col"
              noValidate
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 sm:px-8">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="csr-company"
                      className="text-xs font-semibold"
                    >
                      {t("csr.form.companyName")} *
                    </Label>
                    <IconInput
                      id="csr-company"
                      icon={Building2}
                      placeholder="e.g. ABC Foundation"
                      value={form.companyName}
                      onChange={e => set("companyName")(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="csr-person"
                      className="text-xs font-semibold"
                    >
                      {t("csr.form.contactPerson")} *
                    </Label>
                    <IconInput
                      id="csr-person"
                      icon={User}
                      placeholder="e.g. Rahul Sharma"
                      value={form.contactPerson}
                      onChange={e => set("contactPerson")(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="csr-email"
                      className="text-xs font-semibold"
                    >
                      {t("csr.form.email")} *
                    </Label>
                    <IconInput
                      id="csr-email"
                      icon={Mail}
                      type="email"
                      placeholder="csr@company.com"
                      value={form.email}
                      onChange={e => set("email")(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="csr-mobile"
                      className="text-xs font-semibold"
                    >
                      {t("csr.form.mobile")} *
                    </Label>
                    <IconInput
                      id="csr-mobile"
                      icon={Phone}
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={form.mobile}
                      onChange={e => set("mobile")(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="csr-designation"
                    className="text-xs font-semibold"
                  >
                    {t("csr.form.designation")}
                  </Label>
                  <IconInput
                    id="csr-designation"
                    icon={Briefcase}
                    placeholder="e.g. CSR Manager"
                    value={form.designation}
                    onChange={e => set("designation")(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold">
                    {t("csr.form.interests")} *
                  </Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {CSR_AREAS.map(area => {
                      const active = interests.includes(area.value);
                      return (
                        <button
                          key={area.value}
                          type="button"
                          onClick={() => toggleInterest(area.value)}
                          aria-pressed={active}
                          className={cn(
                            "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors",
                            active
                              ? "bg-primary/5 border-primary text-primary"
                              : "hover:border-primary/40 border-border bg-background"
                          )}
                        >
                          <span
                            className={cn(
                              "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                              active
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                            )}
                          >
                            <area.icon className="h-4 w-4" />
                          </span>
                          <span className="flex-1 font-medium leading-snug">
                            {t(`${area.key}.title`)}
                          </span>
                          {active && <Check className="h-4 w-4 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                      {t("csr.form.contribution")}
                    </Label>
                    <Select
                      value={form.contribution}
                      onValueChange={set("contribution")}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue
                          placeholder={t("csr.form.selectPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {CSR_CONTRIBUTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            {t(o.key)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold">
                      {t("csr.form.duration")}
                    </Label>
                    <Select
                      value={form.duration}
                      onValueChange={set("duration")}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue
                          placeholder={t("csr.form.selectPlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {CSR_DURATIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            {t(o.key)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="csr-message"
                    className="text-xs font-semibold"
                  >
                    {t("csr.form.message")}
                  </Label>
                  <Textarea
                    id="csr-message"
                    rows={3}
                    placeholder={t("csr.form.messagePlaceholder")}
                    value={form.message}
                    onChange={e => set("message")(e.target.value)}
                    className="resize-none rounded-xl"
                  />
                </div>

                <label className="bg-muted/30 flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 text-xs leading-relaxed text-muted-foreground">
                  <Checkbox
                    className="mt-0.5"
                    checked={consent}
                    onCheckedChange={v => setConsent(v === true)}
                  />
                  <span>{t("csr.form.consent")}</span>
                </label>
              </div>

              <div className="flex shrink-0 justify-end border-t border-border bg-card px-6 py-4 sm:px-8">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full px-7 font-bold shadow-md sm:w-auto"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {submitting ? t("csr.form.submitting") : t("csr.form.submit")}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
